import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { getSessions } from '../../services/sessions';
import { getMillEmails, sendMillEmail, sendReminderMillEmail, sendTestMillEmail } from '../../services/millEmails';
import { ENDPOINTS } from '../../utils/constants';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import Button from '../../components/common/Button';
import { formatDate } from '../../utils/formatters';
import './EmployeePage.css';
import './EmployeeListPage.css';
import './MillEmails.css';

const EMAIL_TYPES = {
  ANNOUNCEMENT: 'announcement',
  REMINDER: 'reminder',
};

const MillEmails = () => {
  // ── Data state ───────────────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pastEmails, setPastEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Form state ───────────────────────────────────────────
  const [emailType, setEmailType] = useState(EMAIL_TYPES.ANNOUNCEMENT);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());
  const [customMessage, setCustomMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [testEmail, setTestEmail] = useState('');

  // ── Reminder skip tracking ────────────────────────────────
  const [alreadyOrderedCompanyIds, setAlreadyOrderedCompanyIds] = useState(new Set());

  // ── Action state ─────────────────────────────────────────
  const [isSending, setIsSending] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [alert, setAlert] = useState(null);

  // ── Load initial data ─────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sessionsData, usersData, emailsData] = await Promise.all([
        getSessions({ employee: true }),
        api.get(ENDPOINTS.AUTH.APPROVED_USERS),
        getMillEmails(),
      ]);

      const upcoming = (Array.isArray(sessionsData) ? sessionsData : []).filter(
        (s) => s.status === 'open'
      );
      setSessions(upcoming);
      setCustomers(Array.isArray(usersData.data) ? usersData.data : []);
      setPastEmails(Array.isArray(emailsData) ? emailsData : []);
    } catch {
      setAlert({ type: 'error', message: 'Failed to load data. Please refresh.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Pre-fill subject when switching to reminder + session selected ──────────
  useEffect(() => {
    if (emailType === EMAIL_TYPES.REMINDER && selectedSession) {
      const session = sessions.find((s) => String(s.id) === String(selectedSession));
      if (session) {
        setSubject(`Reminder: ${session.label} — Order by ${formatDate(session.mill_start)}`);
      }
    }
  }, [emailType, selectedSession, sessions]);

  // ── Fetch already-ordered companies when in reminder mode ─────────────────
  useEffect(() => {
    if (emailType !== EMAIL_TYPES.REMINDER || !selectedSession) {
      setAlreadyOrderedCompanyIds(new Set());
      return;
    }
    api
      .get(`${ENDPOINTS.ORDERS.EMPLOYEE.LIST}?session=${selectedSession}&page_size=500`)
      .then((res) => {
        const orders = res.data.results ?? res.data;
        const companyIds = new Set(
          (Array.isArray(orders) ? orders : []).map((o) => o.company)
        );
        setAlreadyOrderedCompanyIds(companyIds);
      })
      .catch(() => setAlreadyOrderedCompanyIds(new Set()));
  }, [emailType, selectedSession]);

  // ── Compute skipped count for the current selection ──────────────────────
  const skippedCount = emailType === EMAIL_TYPES.REMINDER
    ? customers.filter(
        (c) => selectedCustomerIds.has(c.id) && alreadyOrderedCompanyIds.has(c.company_id)
      ).length
    : 0;
  const effectiveRecipientCount = selectedCustomerIds.size - skippedCount;

  // ── Customer selection helpers ────────────────────────────
  const allSelected = customers.length > 0 && selectedCustomerIds.size === customers.length;
  const someSelected = selectedCustomerIds.size > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedCustomerIds(new Set());
    } else {
      setSelectedCustomerIds(new Set(customers.map((c) => c.id)));
    }
  };

  const handleToggleCustomer = (id) => {
    setSelectedCustomerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Send test email ───────────────────────────────────────
  const handleSendTest = async () => {
    if (!selectedSession) {
      setAlert({ type: 'error', message: 'Please select a session first.' });
      return;
    }
    if (!testEmail.trim()) {
      setAlert({ type: 'error', message: 'Please enter a test email address.' });
      return;
    }
    setIsSendingTest(true);
    setAlert(null);
    try {
      await sendTestMillEmail({
        sessionId: parseInt(selectedSession, 10),
        testEmail: testEmail.trim(),
        customMessage,
        type: emailType,
        subject,
      });
      setAlert({ type: 'success', message: `Test email sent to ${testEmail.trim()}.` });
    } catch (err) {
      setAlert({ type: 'error', message: err?.message || 'Failed to send test email.' });
    } finally {
      setIsSendingTest(false);
    }
  };

  // ── Send to selected customers ────────────────────────────
  const handleSendConfirm = async () => {
    setShowConfirm(false);
    setIsSending(true);
    setAlert(null);
    try {
      const isReminder = emailType === EMAIL_TYPES.REMINDER;
      const result = isReminder
        ? await sendReminderMillEmail({
            sessionId: parseInt(selectedSession, 10),
            customerIds: Array.from(selectedCustomerIds),
            subject,
            customMessage,
          })
        : await sendMillEmail({
            sessionId: parseInt(selectedSession, 10),
            customerIds: Array.from(selectedCustomerIds),
            customMessage,
          });

      let message = `Email sent to ${result.sent_count ?? result.recipient_count ?? selectedCustomerIds.size} customer(s).`;
      if (isReminder && result.skipped_count) {
        message += ` ${result.skipped_count} already ordered and were skipped.`;
      }

      setAlert({ type: 'success', message });
      setSelectedCustomerIds(new Set());
      setCustomMessage('');
      if (isReminder) setSubject('');
      const emailsData = await getMillEmails();
      setPastEmails(Array.isArray(emailsData) ? emailsData : []);
    } catch (err) {
      setAlert({ type: 'error', message: err?.message || 'Failed to send emails.' });
    } finally {
      setIsSending(false);
    }
  };

  const handleTypeChange = (type) => {
    setEmailType(type);
    setSubject('');
    setAlreadyOrderedCompanyIds(new Set());
  };

  const canSend = selectedSession && selectedCustomerIds.size > 0 && !isSending
    && (emailType === EMAIL_TYPES.ANNOUNCEMENT || subject.trim());

  if (loading) {
    return (
      <div className="employee-page">
        <Loading text="Loading..." />
      </div>
    );
  }

  return (
    <div className="employee-page mill-emails-page">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="employee-page__header">
        <p className="employee-page__eyebrow">Employee Portal</p>
        <h1 className="employee-page__title">Mill Session Emails</h1>
      </div>

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {/* ── Compose panel ───────────────────────────────────── */}
      <div className="mill-emails__compose emp-list__body emp-list__body--form">

        {/* Type toggle */}
        <div className="mill-emails__type-toggle">
          <button
            type="button"
            className={`mill-emails__type-btn ${emailType === EMAIL_TYPES.ANNOUNCEMENT ? 'mill-emails__type-btn--active' : ''}`}
            onClick={() => handleTypeChange(EMAIL_TYPES.ANNOUNCEMENT)}
          >
            Announcement
          </button>
          <button
            type="button"
            className={`mill-emails__type-btn ${emailType === EMAIL_TYPES.REMINDER ? 'mill-emails__type-btn--active' : ''}`}
            onClick={() => handleTypeChange(EMAIL_TYPES.REMINDER)}
          >
            Reminder
          </button>
        </div>

        <div className="mill-emails__compose-grid">

          {/* Left column: session + subject (reminder) + message + test */}
          <div className="mill-emails__left">
            <div className="mill-emails__field">
              <label className="mill-emails__label" htmlFor="session-select">
                Mill Session
              </label>
              {sessions.length === 0 ? (
                <p className="mill-emails__empty-note">No upcoming sessions available.</p>
              ) : (
                <select
                  id="session-select"
                  className="mill-emails__select"
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                >
                  <option value="">— Select a session —</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} (orders due ~{formatDate(s.mill_start)})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {emailType === EMAIL_TYPES.REMINDER && (
              <div className="mill-emails__field">
                <label className="mill-emails__label" htmlFor="subject">
                  Subject Line
                </label>
                <input
                  id="subject"
                  type="text"
                  className="mill-emails__input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Reminder: June Milling Session — Order by June 5"
                />
              </div>
            )}

            <div className="mill-emails__field">
              <label className="mill-emails__label" htmlFor="custom-message">
                Custom Message <span className="mill-emails__optional">(optional)</span>
              </label>
              <textarea
                id="custom-message"
                className="mill-emails__textarea"
                rows={5}
                placeholder="Add a custom note that will appear in the email body..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
              />
            </div>

            {/* Test email row */}
            <div className="mill-emails__field">
              <label className="mill-emails__label" htmlFor="test-email">
                Send Test Email
              </label>
              <div className="mill-emails__test-row">
                <input
                  id="test-email"
                  type="email"
                  className="mill-emails__input"
                  placeholder="test@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleSendTest}
                  disabled={isSendingTest || !selectedSession}
                >
                  {isSendingTest ? 'Sending...' : 'Send Test'}
                </Button>
              </div>
            </div>
          </div>

          {/* Right column: customer list */}
          <div className="mill-emails__right">
            <div className="mill-emails__customer-header">
              <span className="mill-emails__label">Recipients</span>
              <button
                type="button"
                className="mill-emails__select-all"
                onClick={handleSelectAll}
              >
                <span
                  className={`mill-emails__checkbox ${allSelected ? 'mill-emails__checkbox--checked' : someSelected ? 'mill-emails__checkbox--partial' : ''}`}
                  aria-hidden="true"
                />
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
              <span className="mill-emails__selected-count">
                {selectedCustomerIds.size} / {customers.length} selected
              </span>
            </div>

            <div className="mill-emails__customer-list">
              {customers.length === 0 ? (
                <p className="mill-emails__empty-note">No customers found.</p>
              ) : (
                customers.map((c) => {
                  const alreadyOrdered = emailType === EMAIL_TYPES.REMINDER
                    && alreadyOrderedCompanyIds.has(c.company_id);
                  return (
                    <label
                      key={c.id}
                      className={`mill-emails__customer-row ${alreadyOrdered ? 'mill-emails__customer-row--ordered' : ''}`}
                    >
                      <input
                        type="checkbox"
                        className="mill-emails__checkbox-input"
                        checked={selectedCustomerIds.has(c.id)}
                        onChange={() => handleToggleCustomer(c.id)}
                      />
                      <span
                        className={`mill-emails__checkbox ${selectedCustomerIds.has(c.id) ? 'mill-emails__checkbox--checked' : ''}`}
                        aria-hidden="true"
                      />
                      <span className="mill-emails__customer-name">
                        {c.first_name} {c.last_name}
                        {c.company_name && (
                          <span className="mill-emails__customer-company"> — {c.company_name}</span>
                        )}
                        {alreadyOrdered && (
                          <span className="mill-emails__ordered-badge">ordered</span>
                        )}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            {emailType === EMAIL_TYPES.REMINDER && selectedSession && skippedCount > 0 && (
              <p className="mill-emails__skip-note">
                {skippedCount} of {selectedCustomerIds.size} selected customer{selectedCustomerIds.size === 1 ? '' : 's'} ha{skippedCount === 1 ? 's' : 've'} already ordered and will be skipped automatically.
              </p>
            )}
          </div>
        </div>

        {/* Send button */}
        <div className="mill-emails__send-row">
          <Button
            variant="primary"
            size="medium"
            onClick={() => setShowConfirm(true)}
            disabled={!canSend}
          >
            {isSending
              ? 'Sending...'
              : emailType === EMAIL_TYPES.REMINDER
                ? `Send Reminder to ${effectiveRecipientCount} Customer${effectiveRecipientCount === 1 ? '' : 's'}`
                : `Send to ${selectedCustomerIds.size} Customer${selectedCustomerIds.size === 1 ? '' : 's'}`
            }
          </Button>
        </div>
      </div>

      {/* ── Confirmation overlay ──────────────────────────── */}
      {showConfirm && (
        <div className="mill-emails__overlay" onClick={() => setShowConfirm(false)}>
          <div className="mill-emails__confirm" onClick={(e) => e.stopPropagation()}>
            <h2 className="mill-emails__confirm-title">Confirm Send</h2>
            <p className="mill-emails__confirm-body">
              Send {emailType === EMAIL_TYPES.REMINDER ? 'reminder' : 'announcement'} to{' '}
              <strong>
                {emailType === EMAIL_TYPES.REMINDER ? effectiveRecipientCount : selectedCustomerIds.size} customer{(emailType === EMAIL_TYPES.REMINDER ? effectiveRecipientCount : selectedCustomerIds.size) === 1 ? '' : 's'}
              </strong>?
              {emailType === EMAIL_TYPES.REMINDER && skippedCount > 0 && (
                <> {skippedCount} who already ordered will be skipped.</>
              )}
              {' '}This will send a real email via Resend.
            </p>
            <div className="mill-emails__confirm-actions">
              <Button variant="secondary" size="small" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="small" onClick={handleSendConfirm}>
                Yes, Send
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Past emails history ───────────────────────────── */}
      <div className="mill-emails__history">
        <h2 className="mill-emails__history-title">Send History</h2>

        {pastEmails.length === 0 ? (
          <div className="emp-list__body mill-emails__history-empty">
            <p>No emails sent yet.</p>
          </div>
        ) : (
          <div className="emp-list__body">
            <table className="mill-emails__history-table">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Type</th>
                  <th>Sent By</th>
                  <th>Recipients</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {pastEmails.map((email) => (
                  <tr key={email.id}>
                    <td>{email.session_label ?? email.session}</td>
                    <td>
                      <span className={`mill-emails__type-badge mill-emails__type-badge--${email.type ?? 'announcement'}`}>
                        {email.type === 'reminder' ? 'Reminder' : 'Announcement'}
                      </span>
                    </td>
                    <td>{email.sent_by_name ?? '—'}</td>
                    <td>
                      {email.recipient_count}
                      {email.skipped_count > 0 && (
                        <span className="mill-emails__skipped-note"> ({email.skipped_count} skipped)</span>
                      )}
                    </td>
                    <td>{formatDate(email.sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MillEmails;
